import React, { useState, useEffect } from 'react';
import './AvailabilityCalendar.css';
import tokenManager from '../utils/tokenManager';
import apiConfig from '../utils/apiConfig';

const AvailabilityCalendar = ({ appParams = {} }) => {
	const [currentDate, setCurrentDate] = useState(appParams['min_selectable_date'] || new Date());
	const [availability, setAvailability] = useState({});
	const [loading, setLoading] = useState(true);
	const [lastUpdated, setLastUpdated] = useState(null);
	const [startingPointName, setStartingPointName] = useState(null);

	// appParamsから直接値を取得（更新されたら自動的に反映される）
	const busTypeId = appParams['bus_type_id'] || '4';
	const prefId = appParams['pref_id'] || '8_0';
	const busCount = appParams['bus_count'] || '1';
	const busGuide = appParams['bus_guide'] || '0';
	const fid = appParams['fid'] || '1';
	const useType = appParams['use_type'] || '1';
	const apiUseType = '1';
	const requiredDays = '1';
	const companyType = '0';
	const startTime = appParams['start_time'] || null;
	const endTime = appParams['end_time'] || null;

	// APIからデータを取得
	useEffect(() => {
		const fetchAvailability = async () => {
			setLoading(true);
			try {
				const token = await tokenManager.getToken();
				const baseUrl = await apiConfig.getApiBaseUrl() || 'http://localhost:18090';
				
				const apiParams = buildApiParams(
					currentDate,
					prefId, 
					busTypeId, 
					busCount, 
					busGuide, 
					fid, 
					apiUseType, 
					requiredDays, 
					companyType
				);
				const apiUrl = `${baseUrl}/api/v1/calendar.json${apiParams}`;
				
				// リトライ付きでフェッチ
				const data = await fetchWithRetry(apiUrl, token, 5, 1000);
				setAvailability(parseAvailabilityData(data));
				
				// 出発地名を設定
				if (data['starting_point_name']) {
					setStartingPointName(data['starting_point_name']);
				}
				
				// 更新日時を設定（現在時刻）
				setLastUpdated(new Date(data['last_updated'] || Date.now()));
			} catch (error) {
				console.error('データ取得エラー:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchAvailability();
	}, [currentDate, busTypeId, prefId, busCount, busGuide, fid, useType, apiUseType, requiredDays, companyType, startTime, endTime]);

	// リトライ機能付きのフェッチ
	const fetchWithRetry = async (url, token, maxRetries = 3, delay = 1000) => {
		for (let attempt = 1; attempt <= maxRetries; attempt++) {
			try {
				console.log(`API呼び出し試行 ${attempt}/${maxRetries}`);
				
				const response = await fetch(url, { 
					method: 'GET',
					headers: buildHeaders(token),
					credentials: 'same-origin'
				});
				
				if (!response.ok) {
					// 401エラーの場合は特別処理
					if (response.status === 401) {
						handleApiError(response.status);
						
						// 最初の試行でトークンエラーの場合、新しいトークンで再試行
						if (attempt === 1) {
							console.log('新しいトークンで再試行します...');
							const newToken = await tokenManager.getToken();
							const retryResponse = await fetch(url, {
								method: 'GET',
								headers: buildHeaders(newToken),
								credentials: 'same-origin'
							});
							
							if (retryResponse.ok) {
								return await retryResponse.json();
							}
						}
					}
					
					throw new Error(`HTTP error! status: ${response.status}`);
				}
				
				// 成功したらデータを返す
				return await response.json();
				
			} catch (error) {
				console.warn(`試行 ${attempt} 失敗:`, error.message);
				
				// 最後の試行でもエラーの場合は例外をスロー
				if (attempt === maxRetries) {
					throw new Error(`${maxRetries}回の試行後も失敗しました: ${error.message}`);
				}
				
				// 次の試行前に待機
				await new Promise(resolve => setTimeout(resolve, delay * attempt));
			}
		}
	};

	// APIパラメータを構築
	const buildApiParams = (date, prefId, busTypeId, busCount, busGuide, fid, useType, requiredDays, companyType) => {
		const timestamp = Math.floor(date.getTime() / 1000);
		const params = {
			s_ymd: timestamp,
			e_ymd: timestamp,
			sp_id: prefId,
			btid: busTypeId,
			bus_count: busCount,
			bus_guide: busGuide,
			fid: fid,
			use_type: useType,
			required_days: requiredDays,
			company_type: companyType
		};
		
		return '?' + Object.entries(params)
			.map(([key, value]) => `${key}=${value}`)
			.join('&');
	};

	// HTTPヘッダーを構築
	const buildHeaders = (token) => ({
		'Access-Control-Allow-Origin': '*',
		'Accept': 'application/json',
		'Authorization': `Bearer ${token}`
	});

	// APIエラーハンドリング
	const handleApiError = (status) => {
		if (status === 401) {
			tokenManager.clearToken();
			console.warn('トークンが無効です。再取得を試みます。');
		}
	};

	// JSONデータをカレンダー形式に変換
	const parseAvailabilityData = (data) => {
		const parsed = {};
		Object.entries(data).forEach(([date, status]) => {
			parsed[date] = status >= 3 ? '○' : status === 0 ? '✖' : '△';
		});
		return parsed;
	};

	// 更新日時をフォーマット
	const formatLastUpdated = (date) => {
		if (!date) return '';
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		const hours = String(date.getHours()).padStart(2, '0');
		const minutes = String(date.getMinutes()).padStart(2, '0');
		return `${year}/${month}/${day} ${hours}:${minutes}`;
	};

	// 月を変更（13か月以上先には進めない）
	const changeMonth = (offset) => {
		const newDate = new Date(currentDate);
		newDate.setMonth(currentDate.getMonth() + offset);
		
		// 現在の実際の日付を取得
		const today = new Date();
		today.setDate(1); // 月初に設定
		today.setHours(0, 0, 0, 0);
		
		// 13か月後の月初を計算
		const maxDate = new Date(today);
		maxDate.setMonth(today.getMonth() + 13);
		
		// 3日後の日付を計算（最小選択可能日）
		const minDate = new Date();
		minDate.setDate(minDate.getDate() + 3);
		minDate.setDate(1); // 月初に設定
		minDate.setHours(0, 0, 0, 0);
		
		// 新しい日付が範囲内かチェック
		if (newDate >= minDate && newDate < maxDate) {
			setCurrentDate(newDate);
		}
	};

	// カレンダー表示用のデータを生成
	const generateCalendarDays = () => {
		const year = currentDate.getFullYear();
		const month = currentDate.getMonth();
		const firstDay = new Date(year, month, 1);
		const lastDay = new Date(year, month + 1, 0);
		const daysInMonth = lastDay.getDate();
		const startingDayOfWeek = firstDay.getDay();

		const days = [];

		// 前月の空白を追加
		for (let i = 0; i < startingDayOfWeek; i++) {
			days.push({ day: null, date: null });
		}

		// 選択可能な最小日付（3日後）を計算
		const minSelectableDate = new Date();
		minSelectableDate.setDate(minSelectableDate.getDate() + 3);
		minSelectableDate.setHours(0, 0, 0, 0);

		// startTimeとendTimeから日付範囲を計算
		let rangeStart = null;
		let rangeEnd = null;
		if (startTime !== null && startTime !== undefined && endTime !== null && endTime !== undefined) {
			rangeStart = new Date(startTime * 1000);
			rangeStart.setHours(0, 0, 0, 0);
			rangeEnd = new Date(endTime * 1000);
			rangeEnd.setHours(0, 0, 0, 0);
		}

		// 当月の日付を追加
		for (let day = 1; day <= daysInMonth; day++) {
			const date = new Date(year, month, day);
			const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
			const dayOfWeek = date.getDay();
			
			// 日付をリセットして比較
			const compareDate = new Date(date);
			compareDate.setHours(0, 0, 0, 0);
			
			// 選択可能最小日付より前かどうか
			const isPast = compareDate < minSelectableDate;
			
			// 日付範囲の判定
			let isInRange = false;
			if (rangeStart && rangeEnd) {
				const isStartDate = compareDate.getTime() === rangeStart.getTime();
				const isEndDate = compareDate.getTime() === rangeEnd.getTime();
				
				if (useType === '2') {
					// useTypeが2の場合：初日と最終日のみ
					isInRange = isStartDate || isEndDate;
				} else if (useType === '1' || useType === '3' || useType === '4') {
					// useTypeが1、3、4の場合：範囲全体
					isInRange = compareDate >= rangeStart && compareDate <= rangeEnd;
				}
			}
			
			days.push({
				day,
				date,
				dateKey,
				dayOfWeek,
				isPast,
				isInRange,
				available: availability[dateKey] || '○'
			});
		}

		return days;
	};

	const days = generateCalendarDays();
	const WEEK_DAYS = ['日', '月', '火', '水', '木', '金', '土'];
	const MONTH_NAMES = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
	
	// バスタイプ名を取得
	const getBusTypeName = () => {
		const busTypes = {
			'12': '小型マイクロバス',
			'4': '大型バス',
			'3': '中型バス'
		};
		return busTypes[busTypeId] || `バスタイプ${busTypeId}`;
	};

	// 月送りボタンの有効/無効を判定
	const today = new Date();
	today.setDate(1);
	today.setHours(0, 0, 0, 0);
	
	const minDate = new Date();
	minDate.setDate(minDate.getDate() + 3);
	minDate.setDate(1);
	minDate.setHours(0, 0, 0, 0);
	
	const maxDate = new Date(today);
	maxDate.setMonth(today.getMonth() + 13);
	
	const prevMonthDate = new Date(currentDate);
	prevMonthDate.setMonth(currentDate.getMonth() - 1);
	
	const nextMonthDate = new Date(currentDate);
	nextMonthDate.setMonth(currentDate.getMonth() + 1);
	
	const isPrevDisabled = prevMonthDate < minDate;
	const isNextDisabled = nextMonthDate >= maxDate;

	return (
		<div className="calendar-wrapper">
			<div className="calendar-header">
				<button 
					onClick={() => changeMonth(-1)} 
					className="month-button"
					disabled={isPrevDisabled}
				>
					&lt;
				</button>
				<div className="current-month">
					{currentDate.getFullYear()}年 {MONTH_NAMES[currentDate.getMonth()]}
				</div>
				<button 
					onClick={() => changeMonth(1)} 
					className="month-button"
					disabled={isNextDisabled}
				>
					&gt;
				</button>
			</div>

			{loading ? (
				<div className="loading">読み込み中...</div>
			) : (
				<>
					<div className="calendar-grid">
						{WEEK_DAYS.map((day, index) => (
							<div
								key={`weekday-${index}`}
								className={`weekday ${index === 0 ? 'sunday' : ''} ${index === 6 ? 'saturday' : ''}`}
							>
								{day}
							</div>
						))}
						
						{days.map((dayData, index) => (
							<div
								key={`day-${index}`}
								className={`calendar-day ${!dayData.day ? 'empty' : ''} ${dayData.isPast ? 'past' : ''} ${dayData.isInRange ? 'in-range' : ''}`}
							>
								{dayData.day && (
									<>
										<div className={`day-number ${
											dayData.dayOfWeek === 0 ? 'sunday' : dayData.dayOfWeek === 6 ? 'saturday' : ''
										} ${dayData.isPast ? 'past-number' : ''}`}>
											{dayData.day}
										</div>
									{!dayData.isPast && (
										<div className={`availability-icon ${
											dayData.available === '✖' ? 'unavailable' : 
											dayData.available === '△' ? 'limited' : 
											'available'
										}`}>
											{dayData.available}
										</div>
									)}
									</>
								)}
							</div>
						))}
					</div>
					
					{/* 〇△✖ の説明（凡例） */}
					<div className="calendar-legend">
						<div className="legend-item">
							<span className="legend-symbol available">〇</span>
							<span className="legend-label">3社以上</span>
						</div>
						<div className="legend-item">
							<span className="legend-symbol limited">△</span>
							<span className="legend-label">1-2社</span>
						</div>
					<div className="legend-item">
						<span className="legend-symbol unavailable">✖</span>
						<span className="legend-label">なし</span>
					</div>
					</div>
					
					{/* バスタイプと出発地表示 */}
					<div className="bus-type-info">
						<div>バスタイプ: {getBusTypeName()}</div>
						{startingPointName && (
							<div>出発地: {startingPointName}</div>
						)}
					</div>
					
					{/* データ更新日時 */}
					{lastUpdated && (
						<div className="last-updated">
							更新日時: {formatLastUpdated(lastUpdated)}
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default AvailabilityCalendar;
